import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Sliders, Save, Plus, Trash2 } from 'lucide-react';
import { getToken } from '../lib/api';
import { useToast } from '../components/Toast';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { ...auth().headers, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

export default function SkillRouting() {
  const tenants = useQuery<any[]>({ queryKey: ['tenants'], queryFn: () => fJson('/api/tenants') });
  const [domain, setDomain] = useState('');
  const active = domain || tenants.data?.[0]?.domain_uuid || '';
  const [tab, setTab] = useState<'agents' | 'queues'>('agents');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold inline-flex items-center gap-2"><Sliders size={20} /> Skill-Based Routing</h1>
        <select className="input" value={active} onChange={e => setDomain(e.target.value)}>
          {(tenants.data || []).map(t => <option key={t.domain_uuid} value={t.domain_uuid}>{t.domain_description || t.domain_name}</option>)}
        </select>
      </div>
      <p className="text-sm text-textSecondary">
        Tag agents with named skills (1-10 level). Queues require minimum skill levels and get a per-call ranked
        list of best-fit agents — proper numeric scoring rather than plain skill-name matching.
      </p>
      <div className="flex gap-1 border-b border-border">
        <button onClick={() => setTab('agents')} className={`px-4 py-2 text-sm ${tab === 'agents' ? 'border-b-2 border-primary text-primary' : 'text-textSecondary'}`}>Agent Skills</button>
        <button onClick={() => setTab('queues')} className={`px-4 py-2 text-sm ${tab === 'queues' ? 'border-b-2 border-primary text-primary' : 'text-textSecondary'}`}>Queue Requirements</button>
      </div>
      {tab === 'agents' && <AgentSkills active={active} />}
      {tab === 'queues' && <QueueSkills active={active} />}
    </div>
  );
}

function AgentSkills({ active }: any) {
  const qc = useQueryClient();
  const toast = useToast();
  const users = useQuery<any[]>({ queryKey: ['users-list'], queryFn: () => fJson('/api/users') });
  const inDept = (users.data || []).filter((u: any) => u.domain_uuid === active);
  const [selected, setSelected] = useState<string>('');
  useEffect(() => { if (!selected && inDept[0]) setSelected(inDept[0].extension_uuid); }, [inDept]);

  const skills = useQuery<any[]>({
    queryKey: ['agent-skills', selected],
    queryFn: () => fJson(`/api/skill-routing/agents/${selected}`),
    enabled: !!selected,
  });
  const [list, setList] = useState<any[]>([]);
  useEffect(() => { if (skills.data) setList(skills.data); }, [skills.data]);

  const save = useMutation({
    mutationFn: () => fJson(`/api/skill-routing/agents/${selected}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ skills: list }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['agent-skills', selected] }); toast('success', 'Saved'); },
  });

  return (
    <div className="grid grid-cols-[300px_1fr] gap-4">
      <div className="card p-2 space-y-1">
        {inDept.map((u: any) => (
          <button key={u.extension_uuid} onClick={() => setSelected(u.extension_uuid)}
            className={`w-full text-left px-3 py-2 rounded-md text-sm ${selected === u.extension_uuid ? 'bg-primary text-white' : 'hover:bg-elevated'}`}>
            {u.extension} — {u.name}
          </button>
        ))}
      </div>
      <div className="card p-4 space-y-3">
        <div className="font-medium">Skills</div>
        {list.map((s, i) => (
          <div key={i} className="flex items-center gap-2">
            <input className="input flex-1" value={s.skill_name || ''} onChange={e => { const next = [...list]; next[i] = { ...next[i], skill_name: e.target.value }; setList(next); }} placeholder="spanish / billing / tier2_support" />
            <input type="range" min={1} max={10} value={s.skill_level || 5} onChange={e => { const next = [...list]; next[i] = { ...next[i], skill_level: parseInt(e.target.value) }; setList(next); }} className="w-32" />
            <span className="font-mono text-sm w-8">{s.skill_level || 5}</span>
            <button onClick={() => { const next = [...list]; next.splice(i, 1); setList(next); }} className="btn-ghost p-1 text-danger"><Trash2 size={14} /></button>
          </div>
        ))}
        <button onClick={() => setList([...list, { skill_name: '', skill_level: 5 }])} className="btn-ghost inline-flex items-center gap-1"><Plus size={14} /> Add skill</button>
        <div className="flex justify-end">
          <button onClick={() => save.mutate()} className="btn-primary inline-flex items-center gap-1"><Save size={14} /> Save</button>
        </div>
      </div>
    </div>
  );
}

function QueueSkills({ active }: any) {
  const qc = useQueryClient();
  const toast = useToast();
  const queues = useQuery<any[]>({
    queryKey: ['queues-list', active],
    queryFn: () => fJson(`/api/queues?domain_uuid=${active}`).catch(() => []),
    enabled: !!active,
  });
  const [selected, setSelected] = useState<string>('');
  useEffect(() => { if (!selected && queues.data?.[0]) setSelected(queues.data[0].queue_uuid || queues.data[0].call_center_uuid); }, [queues.data]);

  const data = useQuery<any>({
    queryKey: ['queue-skills', selected],
    queryFn: () => fJson(`/api/skill-routing/queues/${selected}`),
    enabled: !!selected,
  });
  const [reqs, setReqs] = useState<any[]>([]);
  useEffect(() => { if (data.data?.requirements) setReqs(data.data.requirements); }, [data.data]);

  const skillNames = useQuery<any[]>({
    queryKey: ['skill-names', active],
    queryFn: () => fJson(`/api/skill-routing/skills?domain_uuid=${active}`),
  });

  const save = useMutation({
    mutationFn: () => fJson(`/api/skill-routing/queues/${selected}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ requirements: reqs }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['queue-skills', selected] }); toast('success', 'Saved'); },
  });

  return (
    <div className="grid grid-cols-[300px_1fr] gap-4">
      <div className="card p-2 space-y-1">
        {(queues.data || []).map((q: any) => (
          <button key={q.queue_uuid || q.call_center_uuid} onClick={() => setSelected(q.queue_uuid || q.call_center_uuid)}
            className={`w-full text-left px-3 py-2 rounded-md text-sm ${selected === (q.queue_uuid || q.call_center_uuid) ? 'bg-primary text-white' : 'hover:bg-elevated'}`}>
            {q.extension || q.name}
          </button>
        ))}
      </div>
      <div className="card p-4 space-y-3">
        <div className="font-medium">Required Skills</div>
        {reqs.map((r, i) => (
          <div key={i} className="flex items-center gap-2">
            <input className="input flex-1" value={r.skill_name || ''} onChange={e => { const next = [...reqs]; next[i] = { ...next[i], skill_name: e.target.value }; setReqs(next); }} list="known-skills" placeholder="spanish" />
            <span className="text-xs">min level</span>
            <input type="number" min={1} max={10} className="input max-w-[80px]" value={r.min_level || 1} onChange={e => { const next = [...reqs]; next[i] = { ...next[i], min_level: parseInt(e.target.value) }; setReqs(next); }} />
            <span className="text-xs">weight</span>
            <input type="number" step={0.1} className="input max-w-[80px]" value={r.weight || 1.0} onChange={e => { const next = [...reqs]; next[i] = { ...next[i], weight: parseFloat(e.target.value) }; setReqs(next); }} />
            <button onClick={() => { const next = [...reqs]; next.splice(i, 1); setReqs(next); }} className="btn-ghost p-1 text-danger"><Trash2 size={14} /></button>
          </div>
        ))}
        <datalist id="known-skills">
          {(skillNames.data || []).map(s => <option key={s.skill_name} value={s.skill_name} />)}
        </datalist>
        <button onClick={() => setReqs([...reqs, { skill_name: '', min_level: 1, weight: 1.0 }])} className="btn-ghost inline-flex items-center gap-1"><Plus size={14} /> Add requirement</button>

        {!!data.data?.ranked_agents?.length && (
          <div>
            <div className="font-medium text-sm border-b border-border pb-1 mb-2 mt-4">Top-ranked agents</div>
            <table className="table">
              <thead><tr><th>Extension</th><th>Score</th><th>Matched skills</th></tr></thead>
              <tbody>
                {data.data.ranked_agents.slice(0, 10).map((a: any) => (
                  <tr key={a.extension_uuid}>
                    <td>{a.agent_ext}</td>
                    <td className="text-xs">{Number(a.skill_score).toFixed(1)}</td>
                    <td className="text-xs">{a.matched_skill_count} / {a.required_skill_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex justify-end">
          <button onClick={() => save.mutate()} className="btn-primary inline-flex items-center gap-1"><Save size={14} /> Save</button>
        </div>
      </div>
    </div>
  );
}
